import dynamic from 'next/dynamic';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import Button from 'shopper/components/Button';

import SkeletonGroup from 'components/Skeleton/SkeletonGroup';

import useCouponPicker from 'hooks/useCouponPicker';
import useInfiniteQuery from 'hooks/useInfiniteQuery';
import useIntersectionObserver from 'hooks/useIntersectionObserver';

import { sendEvent } from 'lib/gtag';
import placeholder from 'lib/placeholder';

import CouponListItem from './CouponListItem';

import COUPONS from 'constants/coupons';

const CouponListItemSkeleton = dynamic(
  () => import('./CouponListItem/Skeleton'),
  { ssr: false }
);
const EmptyList = dynamic(() => import('./EmptyList'));

const CouponsList = ({
  className,
  gaEventCategory,
  initialCoupons,
  queryFn,
  queryKey,
  shouldShowTruePagination = true,
  seeMoreButtonLink = null,
  withCouponStoreCover = false,
}) => {
  const router = useRouter();
  const { isCouponsCodesVisible } = useCouponPicker(gaEventCategory);
  const [isPaginationEnabled, setPaginationEnabled] = useState(false);
  const fetchNextPageRef = useRef(null);

  const {
    data: coupons,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isSuccess,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey,
    queryFn: (page, { signal }) =>
      queryFn({ page, limit: COUPONS.COUPONS_LIMIT }, { signal }),
    select: (_coupons) =>
      _coupons.filter(
        (coupon) => coupon.couponStatusName !== COUPONS.STATUS.FINISHED
      ),
    getNextPageParam: (lastCoupons, pages) => {
      if (
        lastCoupons.coupons?.at(-1).couponStatusName ===
          COUPONS.STATUS.FINISHED ||
        lastCoupons.coupons?.length < COUPONS.COUPONS_LIMIT ||
        !shouldShowTruePagination
      ) {
        return null;
      }

      return pages.length + 1;
    },
    flattenKey: 'coupons',
    initialData: initialCoupons,
    staleTime: 0,
    cacheTime: 0.5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchIntervalInBackground: false,
    refetchOnReconnect: false,
  });

  useEffect(() => {
    const resetInternalStates = () => {
      setPaginationEnabled(false);
    };

    router.events.on('routeChangeComplete', resetInternalStates);

    return () => {
      router.events.off('routeChangeComplete', resetInternalStates);
    };
  }, []);

  useIntersectionObserver({
    enabled: isPaginationEnabled && hasNextPage,
    target: fetchNextPageRef,
    rootMargin: '1200px',
    threshold: 0,
    onIntersect: fetchNextPage,
  });

  const onEnablePagination = () => {
    if (shouldShowTruePagination) {
      setPaginationEnabled(true);
    }

    sendEvent({ action: 'see_more_coupons', category: gaEventCategory });
  };

  const isListLoading = isFetchingNextPage || isLoading;
  const showPaginate = !isListLoading && !isPaginationEnabled;

  if (isSuccess && coupons.length === 0) {
    return <EmptyList />;
  }

  return (
    <>
      <section className={twMerge('flex flex-col gap-y-6', className)}>
        {isSuccess &&
          coupons.map(
            ({
              couponCode,
              couponDiscountShort,
              couponId,
              couponTag,
              couponTitle,
              storeImage,
              storeName,
              storeSlug,
              visibility,
            }) => (
              <CouponListItem
                key={couponId}
                couponCode={couponCode}
                couponDiscountShort={couponDiscountShort}
                couponId={couponId}
                couponTag={couponTag}
                couponTitle={couponTitle}
                gaEventCategory={gaEventCategory}
                isCouponVisible={isCouponsCodesVisible}
                storeImage={storeImage}
                storeName={storeName}
                storeSlug={storeSlug}
                visibility={visibility}
                withCouponStoreCover={withCouponStoreCover}
              />
            )
          )}
        {showPaginate && shouldShowTruePagination && (
          <Button className="mx-auto" size="size1" onClick={onEnablePagination}>
            {placeholder('actions.loadMoreCoupons')}
          </Button>
        )}
        {seeMoreButtonLink && !shouldShowTruePagination && (
          <NextLink href={seeMoreButtonLink} passHref>
            <Button as="a" className="mx-auto" size="size1">
              {placeholder('actions.loadMoreCoupons')}
            </Button>
          </NextLink>
        )}
        {isListLoading && (
          <SkeletonGroup items={6} skeleton={CouponListItemSkeleton} />
        )}
      </section>
      <div ref={fetchNextPageRef} />
    </>
  );
};

CouponsList.propTypes = {
  gaEventCategory: PropTypes.string.isRequired,
  initialCoupons: PropTypes.shape({
    after: PropTypes.string,
    coupons: PropTypes.arrayOf(
      PropTypes.shape({
        couponId: PropTypes.number.isRequired,
        couponDiscountValue: PropTypes.string.isRequired,
        couponDiscountShort: PropTypes.string.isRequired,
        couponDiscount: PropTypes.string.isRequired,
        couponDiscountOn: PropTypes.string.isRequired,
        couponStatusName: PropTypes.string.isRequired,
        couponInstructions: PropTypes.string.isRequired,
        couponVerified: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
        storeImage: PropTypes.string,
        storeName: PropTypes.string.isRequired,
        storeDomain: PropTypes.string.isRequired,
        storeSlug: PropTypes.string.isRequired,
        couponTag: PropTypes.arrayOf(PropTypes.string.isRequired),
        couponPublished: PropTypes.string,
        couponTitle: PropTypes.string.isRequired,
        couponCode: PropTypes.string.isRequired,
        couponUrl: PropTypes.string.isRequired,
        userName: PropTypes.string,
      })
    ),
  }),
  queryFn: PropTypes.func.isRequired,
  queryKey: PropTypes.array.isRequired,
  seeMoreButtonLink: PropTypes.string,
  shouldShowTruePagination: PropTypes.bool,
  withCouponStoreCover: PropTypes.bool,
};

export default CouponsList;
